import { z } from 'zod';

import { customAttributeOptionsSchema, customAttributesSchema } from './custom-attribute';
import { idSchema } from './id';
import { paginationSchema } from './pagination';
import { skillSchema } from './skill';
import { baseUserSchema } from './user';

const learningOpportunitySkillSchema = z.object({
    id: idSchema,
});

export const withLearningOpportunityIdParameterSchema = z.object({
    learningOpportunityId: idSchema,
});

export const withFeaturedOpportunityIdParameterSchema = z.object({
    featuredOpportunityId: idSchema,
});

export const learningOpportunitiesOptionsSchema = z.object({
    canCreateLearningOpportunities: z.boolean(),
    canGetRecommendedLearningOpportunities: z.boolean(),
    canManageLearningOpportunities: z.boolean(),
    customAttributes: customAttributeOptionsSchema.array(),
});
export type LearningOpportunitiesOptions = z.infer<typeof learningOpportunitiesOptionsSchema>;

export const learningOpportunityOptionsSchema = z.object({
    canDeleteLearningOpportunity: z.boolean(),
    canEditLearningOpportunity: z.boolean(),
    canReadLearningOpportunity: z.boolean(),
});
export type LearningOpportunityOptions = z.infer<typeof learningOpportunityOptionsSchema>;

const learningOpportunityTranslationsSchema = z.object({
    description: z.string().max(100_000).optional(),
    language: z.string().max(5).min(5),
    title: z.string().max(128).min(3),
});

export type LearningOpportunityTranslation = z.infer<typeof learningOpportunityTranslationsSchema>;

export const urlSchema = z.string().transform((data) => {
    if (data.length === 0) {
        return undefined;
    }
    if (z.url().safeParse(data).success) {
        return data;
    }

    return `https://${data}`;
});

export const learningOpportunityStateSchema = z.union([
    z.literal('draft'),
    z.literal('published'),
    z.literal('archived'),
]);

export const learningOpportunityCreateSchema = z.object({
    custom: customAttributesSchema.optional(),
    learningLanguage: z.string().optional(),
    provider: z.string().optional(),
    skills: learningOpportunitySkillSchema.array().optional(),
    state: learningOpportunityStateSchema,
    translations: learningOpportunityTranslationsSchema.array(),
    type: z.literal('course'),
    url: urlSchema.optional(),
});

export type LearningOpportunityCreate = z.infer<typeof learningOpportunityCreateSchema>;

export const baseLearningOpportunitySchema = z.object({
    id: idSchema,
    title: z.string(),
});

export type BaseLearningOpportunity = z.infer<typeof baseLearningOpportunitySchema>;

export const learningOpportunitySchema = baseLearningOpportunitySchema.extend({
    createdAt: z.string(),
    custom: customAttributesSchema.optional(),
    description: z.string().optional(),
    externalId: z.string().optional(),
    featuredBy: baseUserSchema.optional(),
    isNew: z.boolean(),
    language: z.string().max(5).min(5),
    learningLanguage: z.string().optional(),
    ownerName: z.string().optional(),
    provider: z.string().optional(),
    skills: skillSchema.array().optional(),
    state: learningOpportunityStateSchema,
    translations: learningOpportunityTranslationsSchema.array(),
    type: z.literal('course'),
    updatedAt: z.string().optional(),
    url: urlSchema.optional(),
});

export const learningOpportunityListSchema = learningOpportunitySchema.array();

export const learningOpportunitiesResponseSchema = z.object({
    data: learningOpportunityListSchema,
    pagination: paginationSchema,
});

export type LearningOpportunitiesResponse = z.infer<typeof learningOpportunitiesResponseSchema>;

export type LearningOpportunity = z.infer<typeof learningOpportunitySchema>;

export type LearningOpportunityList = z.infer<typeof learningOpportunityListSchema>;

export type LearningOpportunityState = 'all' | 'archived' | 'draft' | 'published';

export type LearningOpportunityType = 'course';

const learningOpportunityCompletionRequestSchema = z.object({
    addSkillsToUser: z.boolean(),
    completedAt: z.string().optional(),
    generateUserCourseHistoryEntry: z.boolean(),
});

export type LearningOpportunityCompletionRequest = z.infer<typeof learningOpportunityCompletionRequestSchema>;

export const learningOpportunityCompletionResponseSchema = z.object({
    completed: z.boolean(),
    completedAt: z.string().optional(),
    learningOpportunityId: z.string(),
    userHistoryCourseId: z.string().optional(),
    userId: z.string(),
});

export type LearningOpportunityCompletionResponse = z.infer<typeof learningOpportunityCompletionResponseSchema>;

const bookmarkedLearningOpportunitySchema = learningOpportunitySchema.extend({
    bookmarkedAt: z.string(),
    bookmarkedBy: z
        .object({
            id: z.uuid(),
            emailAddress: z.email().optional(),
            firstName: z.string(),
            lastName: z.string(),
        })
        .optional(),
});

export type BookmarkedLearningOpportunity = z.infer<typeof bookmarkedLearningOpportunitySchema>;

export const bookmarkedLearningOpportunityListSchema = bookmarkedLearningOpportunitySchema.array();

export const userLearningOpportunitiesBookmarksOptionsSchema = z.object({
    canEditBookmarks: z.boolean(),
    canReadBookmarks: z.boolean(),
});

export type UserLearningOpportunitiesBookmarksOptions = z.infer<typeof userLearningOpportunitiesBookmarksOptionsSchema>;
